import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useReducedMotion } from 'framer-motion';
import ArrowLeft from 'lucide-react/dist/esm/icons/arrow-left.js';
import RotateCw from 'lucide-react/dist/esm/icons/rotate-cw.js';
import { resolveWinterProducts } from '@/data/winterLandingProducts';
import './WinterCampaign.css';

const winterProducts = resolveWinterProducts();
const processSteps = [
  {
    title: 'בוחרים בגד',
    description: 'בוחרים מהקולקציה וממשיכים לצבעים, מידות ואפשרויות העיצוב.',
    image: '/landing/winter/closeup-hood.webp',
  },
  {
    title: 'מעלים עיצוב',
    description: 'מעלים את העיצוב או הלוגו שלכם.',
    image: '/landing/winter/closeup-navy.webp',
  },
  {
    title: 'מאשרים הדמיה',
    description: 'בודקים את מיקום העיצוב והפרטים לפני שממשיכים.',
    image: '/landing/winter/closeup-white.webp',
  },
];

function CatalogPortrait({ image, alt, hidden }) {
  return (
    <picture aria-hidden={hidden ? 'true' : undefined}>
      <source type="image/webp" srcSet={image.webp} />
      <img src={image.src} alt={hidden ? '' : alt} loading="lazy" decoding="async" />
    </picture>
  );
}

function WinterProduct({ selection, reducedMotion }) {
  const { product, images } = selection;
  const [hovered, setHovered] = useState(false);
  const [manualSide, setManualSide] = useState(null);
  const showingBack = manualSide === null ? hovered : manualSide === 'back';
  const photoId = `winter-photo-${product.sku}`;

  return (
    <article className="winter-product" data-winter-sku={product.sku}>
      <Link
        id={photoId}
        to={`/product/${product.sku}`}
        className={`winter-product-photo winter-focus ${showingBack ? 'is-back' : ''} ${reducedMotion ? 'is-still' : ''}`}
        aria-label={`לבחירת ${product.nameHe}, צבעים ומידות`}
        onPointerEnter={(event) => {
          if (event.pointerType === 'mouse' && manualSide === null && !reducedMotion) setHovered(true);
        }}
        onPointerLeave={() => setHovered(false)}
      >
        <span className="winter-portrait-turn">
          <span className="winter-portrait-face winter-portrait-front">
            <CatalogPortrait image={images.front} alt={`${product.nameHe} — מבט מלפנים, תמונת לבישה להמחשה`} hidden={showingBack} />
          </span>
          <span className="winter-portrait-face winter-portrait-back">
            <CatalogPortrait image={images.back} alt={`${product.nameHe} — מבט מאחור, תמונת לבישה להמחשה`} hidden={!showingBack} />
          </span>
        </span>
      </Link>
      <button
        type="button"
        className="winter-turn-control winter-focus"
        aria-label={`להצגת ${showingBack ? 'החזית' : 'הגב'} של ${product.nameHe}`}
        aria-controls={photoId}
        aria-pressed={showingBack}
        onClick={() => {
          setManualSide(showingBack ? 'front' : 'back');
          setHovered(false);
        }}
      >
        <RotateCw size={17} aria-hidden="true" />
        <span className={!showingBack ? 'is-current' : ''}>חזית</span>
        <span aria-hidden="true">/</span>
        <span className={showingBack ? 'is-current' : ''}>גב</span>
      </button>
      <h3>{product.nameHe}</h3>
      <Link to={`/product/${product.sku}`} className="winter-product-action winter-focus">
        לצבעים, מידות ועיצוב
        <ArrowLeft size={17} aria-hidden="true" />
      </Link>
    </article>
  );
}

export default function WinterCampaign() {
  const reducedMotion = useReducedMotion();
  return (
    <div className="winter-campaign" dir="rtl">
      <section className="winter-hero" aria-labelledby="winter-title">
        <div className="winter-hero-inner">
          <div className="winter-hero-copy">
            <p className="winter-eyebrow">ביגוד חורף בעיצוב אישי</p>
            <h1 id="winter-title"><span>החורף שלכם.</span><span>העיצוב שלכם.</span></h1>
            <p className="winter-hero-description">קפוצ׳ונים, סווטשירטים, פליזים, סופטשל וכובעי חורף עם העיצוב או הלוגו שלכם.</p>
            <div className="winter-hero-actions">
              <a href="#winter-products" className="winter-primary winter-focus">בוחרים בגד ומתחילים<ArrowLeft size={24} aria-hidden="true" /></a>
              <Link to="/contact" className="winter-help winter-focus">צריכים עזרה? דברו איתנו</Link>
            </div>
          </div>
          <figure className="winter-hero-photo">
            <img src="/landing/winter/hero.webp" width="620" height="620" alt="המחשה של קפוצ׳ון אפור, סווטשירט נייבי וחולצה ארוכה לבנה, לפני מיתוג" fetchpriority="high" loading="eager" />
          </figure>
        </div>
      </section>
      <section className="winter-story" aria-labelledby="winter-story-title">
        <h2 id="winter-story-title" className="sr-only">מהבחירה ועד בדיקת העיצוב</h2>
        <div className="winter-story-inner">
          <figure className="winter-story-detail">
            <img
              src="/landing/winter/fabric-detail.webp"
              width="900"
              height="900"
              alt="המחשת תקריב של פרטי הביגוד לחורף בגווני אפור, נייבי ולבן"
              loading="lazy"
              decoding="async"
            />
            <figcaption>תקריבי הבד להמחשה.</figcaption>
          </figure>
          <ol className="winter-story-steps">
            {processSteps.map(({ title, description, image }) => (
              <li key={title}>
                <div className="winter-story-copy">
                  <h3>{title}</h3>
                  <p>{description}</p>
                </div>
                <img src={image} width="600" height="450" alt="" aria-hidden="true" loading="lazy" decoding="async" />
              </li>
            ))}
          </ol>
        </div>
      </section>
      <section id="winter-products" className="winter-products" aria-labelledby="winter-products-title">
        <div className="winter-section-inner">
          <h2 id="winter-products-title">על מה תרצו את העיצוב שלכם?</h2>
          <div className="winter-product-grid">
            {winterProducts.map((selection) => <WinterProduct key={selection.product.sku} selection={selection} reducedMotion={reducedMotion} />)}
          </div>
          <p className="winter-catalog-note">תמונות לבישה להמחשה. צבעים, מידות ומחיר בוחרים בדף המוצר.</p>
        </div>
      </section>
    </div>
  );
}
